import * as UI from "@iyk/ui"
import * as React from "react"

import { Dialog as BaseDialog } from "@base-ui/react"
import { useDerivedState } from "../hooks/useDerivedState.ts"
import { useMediaQuery } from "../hooks/useMediaQuery.tsx"
import { Drawer } from "./Drawer.tsx"
import { ScrollArea } from "./ScrollArea.tsx"

const Root = ({ children, open = false, onOpenChange, onOpenChangeComplete }: Dialog.Root.Props) => {
  const [isOpen, setIsOpen] = useDerivedState(open, () => open, [open])
  const isDesktop = useMediaQuery(`(min-width: 640px)`)

  return (
    <Context.Provider value={{ isDesktop, isOpen, setIsOpen }}>
      {isDesktop ? (
        <BaseDialog.Root
          open={isOpen}
          onOpenChange={(open) => {
            setIsOpen(open)
            onOpenChange?.(open)
          }}
          onOpenChangeComplete={onOpenChangeComplete}
        >
          {children}
        </BaseDialog.Root>
      ) : (
        <Drawer.Root
          open={isOpen}
          onOpenChange={(open) => {
            setIsOpen(open)
            onOpenChange?.(open)
          }}
        >
          {children}
        </Drawer.Root>
      )}
    </Context.Provider>
  )
}

const Trigger = (props: Dialog.Trigger.Props) => {
  const { isDesktop } = useContext()

  if (isDesktop) {
    return <BaseDialog.Trigger {...props} />
  }

  return <Drawer.Trigger {...props} />
}

function Content({ className, children, ...props }: Dialog.Content.Props) {
  const { isDesktop } = useContext()

  if (isDesktop) {
    return (
      <BaseDialog.Portal data-slot="dialog-portal">
        <BaseDialog.Backdrop
          data-slot="dialog-overlay"
          className="fixed inset-0 bg-[rgba(25,25,25,0.8)] backdrop-blur transition-all duration-200 [&[data-ending-style]]:opacity-0 [&[data-starting-style]]:opacity-0"
        />
        <BaseDialog.Popup
          data-slot="dialog-content"
          className={UI.cx(
            "fixed z-50 overflow-hidden",
            "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
            "grid grid-rows-[minmax(0,1fr)]",
            "max-h-[calc(100dvh-2rem)] w-full max-w-[calc(100%-2rem)] sm:max-w-md",
            "bg-gray-1 shadow-[0_0_15px_0_rgba(238,238,238,0.05)] rounded-lg",
            "duration-200 outline-none sm:scale-[calc(1-0.1*var(--nested-dialogs))]",
            "data-[starting-style]:translate-y-[-50%] data-[starting-style]:scale-95 data-[starting-style]:opacity-0",
            "data-[ending-style]:translate-y-[-50%] data-[ending-style]:scale-95 data-[ending-style]:opacity-0",
            className
          )}
          {...props}
        >
          <ScrollArea className="h-full min-h-0">
            <div className="flex flex-col gap-4 p-4">{children}</div>
          </ScrollArea>

          <BaseDialog.Close className="size-7 bg-gray-1 hover:bg-gray-3 rounded flex items-center justify-center absolute top-2 right-2 z-50">
            <UI.Icon.Close />
          </BaseDialog.Close>
        </BaseDialog.Popup>
      </BaseDialog.Portal>
    )
  }

  return (
    <Drawer.Content>
      <div className={UI.cx("flex-1 min-h-0 flex flex-col gap-3", className)}>{children}</div>
    </Drawer.Content>
  )
}

const Header = ({ title, description }: Dialog.Header.Props) => (
  <div className="flex flex-col gap-0.5">
    <h2 className="text-sm">{title}</h2>
    {description && <p className="text-sm text-gray-11">{description}</p>}
  </div>
)

const ButtonGroup = ({ children, reverseOnMobile = true }: Dialog.ButtonGroup.Props) => (
  <div className={classForButtonGroup({ reverseOnMobile })}>{children}</div>
)
const classForButtonGroup = UI.cva(["flex md:justify-end gap-2 pt-2"], {
  variants: {
    reverseOnMobile: {
      true: "flex-col-reverse md:flex-row",
      false: "flex-col md:flex-row"
    }
  }
})

export namespace Dialog {
  export namespace Root {
    export type Props = {
      children: React.ReactNode
      open?: boolean
      onOpenChange?: (open: boolean) => void
      onOpenChangeComplete?: (open: boolean) => void
    }
  }

  export namespace Trigger {
    export type Props = {
      children: React.ReactNode
      className?: string
    }
  }
  export namespace Content {
    export type Props = {
      children: React.ReactNode
      className?: string
    }
  }

  export namespace ButtonGroup {
    export type Props = {
      children: React.ReactNode
      reverseOnMobile?: boolean
    }
  }

  export namespace Header {
    export type Props = {
      title: string
      description?: string
    }
  }
}

export const Dialog = {
  Root,
  Trigger,
  Content,
  Header,
  ButtonGroup,
  Close: BaseDialog.Close
}

const noopContext: Context = {
  isDesktop: false,
  isOpen: false,
  setIsOpen: () => {}
}

const Context = React.createContext<Context>(noopContext)
const useContext = () => React.use(Context)

type Context = {
  isDesktop: boolean
  isOpen: boolean
  setIsOpen: (open: boolean) => void
}
